import type { Project } from "./project.ts";

/**
 * A set of examples served with the site: `index.json` and the files it lists,
 * under one site path. The deployment's own examples are under `examples/`
 * and ESP-IDF's under `idf-examples/`.
 *
 * Plain files and not an asset archive, so a project can be opened before the
 * toolchain has arrived and without unpacking a gigabyte. The index is what a
 * caller reads to know which examples there are, what to call each one, which
 * chips it builds for and what it is made of; `packages/web` writes it. The
 * welcome window lists the index and fetches an example when it is chosen.
 */

export interface ExampleIndex {
    examples: ExampleEntry[];
}

export interface ExampleEntry {
    /**
     * The directory it is served from, relative to the index, and the name the
     * project is opened under. ESP-IDF's examples are nested, as in
     * "get-started/hello_world".
     */
    name: string;
    /** The heading of the example's README, or its name when it has none. */
    title: string;
    /** The first sentence of the README's first paragraph, in inline Markdown, or empty. */
    description: string;
    /** The README's "Supported Targets", as target names ("esp32c3"); absent without the table. */
    targets?: string[];
    files: ExampleFile[];
}

export interface ExampleFile {
    /** Relative to the example's directory, e.g. "main/main.c". */
    path: string;
    bytes: number;
}

export async function fetchExampleIndex(baseUrl: string, signal?: AbortSignal): Promise<ExampleIndex> {
    const response = await fetch(`${baseUrl}/index.json`, { signal });
    if (!response.ok) {
        throw new Error(`no example index at ${baseUrl}/index.json (${response.status})`);
    }
    return (await response.json()) as ExampleIndex;
}

/** One example, as a project to open. */
export async function fetchExample(baseUrl: string, name: string, signal?: AbortSignal): Promise<Project> {
    const index = await fetchExampleIndex(baseUrl, signal);
    const entry = index.examples.find((example) => example.name === name);
    if (entry === undefined) {
        const known = index.examples.map((example) => example.name).join(", ");
        throw new Error(`no example ${name} at ${baseUrl}; there is ${known}`);
    }
    const files = await Promise.all(
        entry.files.map(async (file) => {
            const url = `${baseUrl}/${encodePath(entry.name)}/${encodePath(file.path)}`;
            const response = await fetch(url, { signal });
            if (!response.ok) throw new Error(`cannot fetch ${url} (${response.status})`);
            return { path: file.path, bytes: new Uint8Array(await response.arrayBuffer()) };
        }),
    );
    return { name: entry.name, files };
}

function encodePath(path: string): string {
    return path.split("/").map(encodeURIComponent).join("/");
}
